/**
 * PreviewVariablesBuilder — sample values for editor/canvas previews (K3, D64).
 *
 * The runtime substitutes `@variable` tokens with real user answers
 * (web-shell V2VariableResolver / buildMergedVariables). Editor surfaces have
 * no user answers, so previews showed raw `@user_goal` tokens (gap G3). This
 * builder derives a SAMPLE value per variable so operators see personalization
 * while editing. Derivation chain per variable:
 *
 *   1. content fallback — localized `defaultValue` (string/number/boolean/date)
 *      or the `isDefault` option (enum/enum[]);
 *   2. first option label (enum/enum[] without an isDefault flag);
 *   3. bracketed variable name (`[User Goal]`) — never a raw `@` token.
 *
 * Emits every reference form the runtime supports: `id`, `id.label`,
 * `id.value`, and `id.<slotKey>` for text content slots. Pure derivation —
 * no runtime/web-shell semantics change (preview-only).
 */
import type { FunnelVariable } from '@segmently/schema-models/v2';

export type PreviewVariables = Record<string, string>;

/** Slot keys the runtime reserves — never emitted as content-slot refs. */
const RESERVED_SLOT_KEYS = new Set(['value', 'label', 'first', 'last', 'all']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Resolve a locale-keyed text map: exact locale → base language → en → first. */
function resolveLocalizedText(map: Record<string, unknown>, locale: string): string | undefined {
  const direct = map[locale];
  if (typeof direct === 'string' && direct) return direct;
  const base = locale.split('-')[0];
  for (const key of Object.keys(map)) {
    if (key === base || key.split('-')[0] === base) {
      const value = map[key];
      if (typeof value === 'string' && value) return value;
    }
  }
  const en = map.en ?? map['en-US'];
  if (typeof en === 'string' && en) return en;
  const first = Object.values(map).find((value) => typeof value === 'string' && value);
  return typeof first === 'string' ? first : undefined;
}

function bracketedName(variable: FunnelVariable): string {
  const name = (variable as { name?: string }).name || (variable as { id?: string }).id || 'variable';
  return `[${name}]`;
}

function resolveDefaultValueSample(variable: FunnelVariable, locale: string): string | undefined {
  const raw = (variable as { defaultValue?: unknown }).defaultValue;
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw === 'string' && raw) return raw;
  if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
  if (isRecord(raw)) return resolveLocalizedText(raw, locale);
  return undefined;
}

interface OptionLike {
  id?: string;
  label?: string;
  isDefault?: boolean;
  content?: Record<string, unknown>;
}

function optionSample(variable: FunnelVariable): OptionLike | undefined {
  const options = (variable as { options?: OptionLike[] }).options;
  if (!Array.isArray(options) || options.length === 0) return undefined;
  return options.find((option) => option && option.isDefault === true) ?? options[0];
}

/**
 * Build the preview substitution map for a variable set.
 * Keys mirror the runtime reference forms so `substituteVariables` resolves
 * previews exactly like production text.
 */
export function buildPreviewVariables(
  variables: Record<string, FunnelVariable> | undefined | null,
  locale: string,
): PreviewVariables {
  const result: PreviewVariables = {};
  if (!variables) return result;

  for (const [variableId, variable] of Object.entries(variables)) {
    if (!variable || typeof variable !== 'object') continue;
    const type = (variable as { type?: string }).type;
    let sample: string | undefined;
    let sampleLabel: string | undefined;
    let sampleValue: string | undefined;

    if (type === 'enum' || type === 'enum[]') {
      const option = optionSample(variable);
      if (option) {
        sample = option.label || option.id;
        sampleLabel = option.label || option.id;
        sampleValue = option.id;
        // Text content slots of the sample option → `id.slotKey` refs.
        if (isRecord(option.content)) {
          for (const [slotKey, slot] of Object.entries(option.content)) {
            if (RESERVED_SLOT_KEYS.has(slotKey)) continue;
            const text = isRecord(slot) && isRecord((slot as { text?: unknown }).text)
              ? resolveLocalizedText((slot as { text: Record<string, unknown> }).text, locale)
              : undefined;
            if (text) result[`${variableId}.${slotKey}`] = text;
          }
        }
      }
    } else {
      sample = resolveDefaultValueSample(variable, locale);
    }

    const fallback = bracketedName(variable);
    result[variableId] = sample || fallback;
    result[`${variableId}.label`] = sampleLabel || sample || fallback;
    if (sampleValue) result[`${variableId}.value`] = sampleValue;
  }

  return result;
}
